import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LANGUAGE_TAGS, directionOf, type Language } from '@voxa/shared';
import { en, type Dictionary } from './en.ts';
import { ru } from './ru.ts';
import { es } from './es.ts';
import { nl } from './nl.ts';
import { de } from './de.ts';
import { fr } from './fr.ts';
import { ca } from './ca.ts';
import { he } from './he.ts';

/**
 * Interface language, which is deliberately not the conversation language: a
 * Russian speaker may well be running an English interface, and the pair being
 * translated is a property of the room, not of the reader.
 *
 * English is the default. Guessing from `navigator.language` would be a nicer
 * greeting but a worse demo — the person showing this needs to know what the
 * first screen will say before they open it.
 */
/**
 * `satisfies` rather than a bare list: an interface language must also be a
 * language the app knows about, because its name in the picker comes from
 * LANGUAGE_LABELS. The two sets happen to coincide today; the constraint is
 * what keeps that from being an accident nobody notices breaking.
 */
export const UI_LANGUAGES = [
  'en',
  'ru',
  'es',
  'nl',
  'de',
  'fr',
  'ca',
  'he',
] as const satisfies readonly Language[];
export type UiLanguage = (typeof UI_LANGUAGES)[number];

// Typed by UiLanguage, so adding a locale above without writing its dictionary
// is a compile error rather than a blank interface.
const DICTIONARIES: Record<UiLanguage, Dictionary> = { en, ru, es, nl, de, fr, ca, he };
const STORAGE_KEY = 'voxa.ui-language';

interface I18n {
  language: UiLanguage;
  setLanguage(language: UiLanguage): void;
  t: Dictionary;
}

const I18nContext = createContext<I18n | null>(null);

function readStored(): UiLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null && (UI_LANGUAGES as readonly string[]).includes(stored)) {
      return stored as UiLanguage;
    }
  } catch {
    // Private mode, or storage blocked. The default is not worth an error.
  }
  return 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<UiLanguage>(readStored);

  const setLanguage = useCallback((next: UiLanguage) => {
    setLanguageState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies to this tab; it just will not be remembered.
    }
  }, []);

  // Screen readers and hyphenation both key off `lang`; the layout keys off
  // `dir`. Mirroring for Hebrew needs no stylesheet work because there is no
  // left or right in it — the layout is flex and grid, which follow `dir` on
  // their own. Adding a `margin-left` later is what would break it.
  useEffect(() => {
    document.documentElement.lang = LANGUAGE_TAGS[language];
    document.documentElement.dir = directionOf(language);
  }, [language]);

  const value = useMemo<I18n>(
    () => ({ language, setLanguage, t: DICTIONARIES[language] }),
    [language, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n was called outside I18nProvider');
  return value;
}

/** Shorthand for the common case of only needing the strings. */
export const useT = (): Dictionary => useI18n().t;

export type { Dictionary };
